// 📄 pages/MiEspacio/components/TareaFormModal.tsx
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save } from 'lucide-react';
import { Tarea, TareaData, PRIORIDADES_TAREA, CATEGORIAS_TAREA } from '../../../types/miEspacio.ts';

interface TareaFormModalProps {
  // Presente = modo edición; ausente = modo creación.
  tarea?: Tarea | null;
  onClose: () => void;
  onSave: (data: TareaData) => Promise<void>;
}

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const toDateInputValue = (value: any): string => {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  return date ? date.toISOString().slice(0, 10) : '';
};

export default function TareaFormModal({ tarea, onClose, onSave }: TareaFormModalProps) {
  const editando = !!tarea;
  const [titulo, setTitulo] = useState(tarea?.titulo || '');
  const [prioridad, setPrioridad] = useState(tarea?.prioridad || PRIORIDADES_TAREA[1]);
  const [fechaLimiteInput, setFechaLimiteInput] = useState(toDateInputValue(tarea?.fechaLimite));
  const [categoria, setCategoria] = useState(tarea?.categoria || CATEGORIAS_TAREA[CATEGORIAS_TAREA.length - 1]);
  const [notas, setNotas] = useState(tarea?.notas || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim()) { setError('La descripción del pendiente es obligatoria.'); return; }
    setError(null);
    setGuardando(true);
    try {
      await onSave({
        titulo,
        completada: tarea?.completada || false,
        prioridad,
        fechaLimite: fechaLimiteInput ? new Date(`${fechaLimiteInput}T00:00:00`) : null,
        categoria,
        notas
      });
    } catch (err: any) {
      setError(err.message || 'Error al guardar el pendiente.');
    } finally {
      setGuardando(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))]">
          <h3 className="text-lg font-medium text-white">{editando ? 'Editar pendiente' : 'Nuevo pendiente'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Descripción</label>
            <input
              className={inputClass}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="¿Qué hay que hacer?"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Prioridad</label>
              <select className={inputClass} value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>
                {PRIORIDADES_TAREA.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Categoría</label>
              <select className={inputClass} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                {CATEGORIAS_TAREA.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Fecha límite (opcional)</label>
            <input type="date" className={inputClass} value={fechaLimiteInput} onChange={(e) => setFechaLimiteInput(e.target.value)} />
          </div>

          <div>
            <label className={labelClass}>Notas adicionales (opcional)</label>
            <textarea
              className={`${inputClass} min-h-[80px]`}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Contexto adicional — solo se ve al expandir el pendiente."
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              <Save className="w-4 h-4" />
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
